import _ from 'lodash';
import { combineReducers } from 'redux';
import { getJSON } from 'redux-api-middleware';

import apiGenerator from '../apiGenerator';
import { ItemType, STOCK_ITEM_NULL_LABEL } from '../../config/constants';
import Item, { itemToApiMap } from '../../models/Item';
import { mapModelToApi, mapApiToModel } from '../../models/modelMapper';
import { fetchTransactions, fetchFinances } from './transactions';

// Actions
const ADD_NEW_ITEM_REQUEST = 'ctm/items/ADD_NEW_ITEM_REQUEST';
export const ADD_NEW_ITEM_SUCCESS = 'ctm/items/ADD_NEW_ITEM_SUCCESS';
const ADD_NEW_ITEM_FAILURE = 'ctm/items/ADD_NEW_ITEM_FAILURE';
const UPDATE_ITEM_REQUEST = 'ctm/items/UPDATE_ITEM_REQUEST';
export const UPDATE_ITEM_SUCCESS = 'ctm/items/UPDATE_ITEM_SUCCESS';
const UPDATE_ITEM_FAILURE = 'ctm/items/UPDATE_ITEM_FAILURE';
const DELETE_ITEM_REQUEST = 'ctm/items/DELETE_ITEM_REQUEST';
export const DELETE_ITEM_SUCCESS = 'ctm/items/DELETE_ITEM_SUCCESS';
const DELETE_ITEM_FAILURE = 'ctm/items/DELETE_ITEM_FAILURE';
const GET_SUBCATEGORY_ATTRS_REQUEST = 'ctm/items/GET_SUBCATEGORY_ATTRS_REQUEST';
const GET_SUBCATEGORY_ATTRS_SUCCESS = 'ctm/items/GET_SUBCATEGORY_ATTRS_SUCCESS';
const GET_SUBCATEGORY_ATTRS_FAILURE = 'ctm/items/GET_SUBCATEGORY_ATTRS_FAILURE';
const GET_STOCK_SUBCATEGORY_ATTRS_REQUEST = 'ctm/items/GET_STOCK_SUBCATEGORY_ATTRS_REQUEST';
const GET_STOCK_SUBCATEGORY_ATTRS_SUCCESS = 'ctm/items/GET_STOCK_SUBCATEGORY_ATTRS_SUCCESS';
const GET_STOCK_SUBCATEGORY_ATTRS_FAILURE = 'ctm/items/GET_STOCK_SUBCATEGORY_ATTRS_FAILURE';

// Action Creators
export function createNewItem(item, onSuccess, onFailure) {
  const newItem = new Item(item);

  return (dispatch) => {
    dispatch(apiGenerator({
      endpoint: 'collections',
      method: 'POST',
      body: mapModelToApi(newItem, itemToApiMap),
      types: [
        ADD_NEW_ITEM_REQUEST,
        {
          type: ADD_NEW_ITEM_SUCCESS,
          payload: (action, state, res) =>
            getJSON(res).then(json => mapApiToModel(json, itemToApiMap, Item)),
        },
        ADD_NEW_ITEM_FAILURE,
      ],
    }))
      .then((r) => {
        if (r.error) {
          onFailure(r.payload);
        } else {
          onSuccess(r.payload);
          dispatch(fetchTransactions());
          dispatch(fetchFinances());
        }
      })
      .catch(onFailure);
  };
}

export function updateItem(item, onSuccess, onFailure) {
  const updatingItem = new Item(item);

  return (dispatch) => {
    dispatch(apiGenerator({
      endpoint: `collections/${updatingItem.id}`,
      method: 'PUT',
      body: mapModelToApi(updatingItem, itemToApiMap),
      types: [
        UPDATE_ITEM_REQUEST,
        {
          type: UPDATE_ITEM_SUCCESS,
          payload: (action, state, res) =>
            getJSON(res).then(json => mapApiToModel(json, itemToApiMap, Item)),
        },
        UPDATE_ITEM_FAILURE,
      ],
    }))
      .then((r) => {
        if (r.error) {
          onFailure(r.payload);
        } else {
          onSuccess(r.payload);
          dispatch(fetchTransactions());
          dispatch(fetchFinances());
        }
      })
      .catch(onFailure);
  };
}

export function deleteItem(itemId, onSuccess, onFailure) {
  return (dispatch) => {
    dispatch(apiGenerator({
      endpoint: `collections/${itemId}`,
      method: 'DELETE',
      types: [
        DELETE_ITEM_REQUEST,
        {
          type: DELETE_ITEM_SUCCESS,
          meta: { id: itemId },
        },
        DELETE_ITEM_FAILURE,
      ],
    }))
      .then((r) => {
        if (r.error) {
          onFailure(r.payload);
        } else {
          onSuccess();
          dispatch(fetchTransactions());
          dispatch(fetchFinances());
        }
      })
      .catch(onFailure);
  };
}

export function querySubcategories(path, isTerminal) {
  // Funky, but convert "No Value" entries back to null for the stock item search
  const finalPath = path.map(p => (p === STOCK_ITEM_NULL_LABEL ? 'null' : encodeURIComponent(p))).join('/');

  return apiGenerator({
    endpoint: `stock-meta/${finalPath}`,
    method: 'GET',
    types: [
      GET_SUBCATEGORY_ATTRS_REQUEST,
      {
        type: GET_SUBCATEGORY_ATTRS_SUCCESS,
        payload: (action, state, res) => getJSON(res).then(json => json),
        meta: { path, isTerminal },
      },
      GET_SUBCATEGORY_ATTRS_FAILURE,
    ],
  });
}

export function queryStockSubcategories(path, isTerminal) {
  // Funky, but convert "No Value" entries back to null for the stock item search
  const finalPath = path.map(p => (p === STOCK_ITEM_NULL_LABEL ? 'null' : encodeURIComponent(p))).join('/');

  return apiGenerator({
    endpoint: `stock-items/${finalPath}`,
    method: 'GET',
    types: [
      GET_STOCK_SUBCATEGORY_ATTRS_REQUEST,
      {
        type: GET_STOCK_SUBCATEGORY_ATTRS_SUCCESS,
        payload: (action, state, res) => getJSON(res).then(json => json),
        meta: { path, isTerminal },
      },
      GET_STOCK_SUBCATEGORY_ATTRS_FAILURE,
    ],
  });
}

// Reducers
export function isFetchingStockData(state = false, action) {
  switch (action.type) {
    case GET_SUBCATEGORY_ATTRS_REQUEST:
      if (action.error) {
        return false;
      }
      return true;
    case GET_SUBCATEGORY_ATTRS_SUCCESS:
    case GET_SUBCATEGORY_ATTRS_FAILURE:
      return false;
    default:
      return state;
  }
}

export function isFetchingStockDataSub(state = false, action) {
  switch (action.type) {
    case GET_STOCK_SUBCATEGORY_ATTRS_REQUEST:
      if (action.error) {
        return false;
      }
      return true;
    case GET_STOCK_SUBCATEGORY_ATTRS_SUCCESS:
    case GET_STOCK_SUBCATEGORY_ATTRS_FAILURE:
      return false;
    default:
      return state;
  }
}

const stockDataMapInitialState = _.mapValues(ItemType, () => ({}));
export function stockDataMap(state = stockDataMapInitialState, action) {
  switch (action.type) {
    case GET_SUBCATEGORY_ATTRS_SUCCESS: {
      const newState = Object.assign({}, state);
      const values = action.payload.reduce((accum, curr) => {
        // This is a little funky: normally map the current item directly as a key.
        // If isTerminal response, map stock item IDs as key with item object as value.
        if (action.meta.isTerminal) {
          accum[curr.id] = curr;
        } else {
          // This is also funky. A null value is actually valid, and should be mapped
          // to some user-friendly term to indicate no known value in the stock DB
          accum[curr || STOCK_ITEM_NULL_LABEL] = {};
        }
        return accum;
      }, {});

      const decodedPath = action.meta.path.map(p => decodeURIComponent(p));
      return _.merge(newState, _.setWith({}, decodedPath, values, () => ({})));
    }
    default:
      return state;
  }
}

const searchDataMapInitialState = _.mapValues(ItemType, () => ({}));
export function searchDataMap(state = searchDataMapInitialState, action) {
  switch (action.type) {
    case GET_STOCK_SUBCATEGORY_ATTRS_SUCCESS: {
      const newState = Object.assign({});
      const values = action.payload.reduce((accum, curr) => {
        accum[curr.id] = curr;
        return accum;
      }, {});

      const decodedPath = action.meta.path.map(p => decodeURIComponent(p));
      return _.merge(newState, _.setWith({}, decodedPath, values, () => ({})));
    }
    default:
      return state;
  }
}

export function isFetchingItem(state = false, action) {
  switch (action.type) {
    case ADD_NEW_ITEM_REQUEST:
    case UPDATE_ITEM_REQUEST:
    case DELETE_ITEM_REQUEST:
      return true;
    case ADD_NEW_ITEM_SUCCESS:
    case ADD_NEW_ITEM_FAILURE:
    case UPDATE_ITEM_SUCCESS:
    case UPDATE_ITEM_FAILURE:
    case DELETE_ITEM_SUCCESS:
    case DELETE_ITEM_FAILURE:
      return false;
    default:
      return state;
  }
}

export default combineReducers({
  stockDataMap,
  searchDataMap,
  isFetchingStockData,
  isFetchingItem,
});



// WEBPACK FOOTER //
// ./src/redux/ducks/items.js